import format from 'date-fns/format';
import isObject from 'lodash/isObject';
import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import tail from 'lodash/tail';
import includes from 'lodash/includes';

import { getIntlNumberFormat } from '../intl-helpers';
import { formatKeyValue } from '../formatters';
import { store } from '../index';
import { selectors } from '../reducers/appReducer';

const baseIntl = getIntlNumberFormat();

/**
 * Get del valore
 *
 * Aggiunto controllo per visualizzare il code dei campi che sono soggetti al code solo se è abilitata la funzione (showFilterCode)
 * e se il campo è presente in filterCodeFieldList
 *
 * @param detail
 * @param prop
 * @param locale
 * @param productProperties
 * @param i18n
 * @param priceDecimalPrecision
 * @returns {null|string|*|undefined|Exclude<GetFieldType<{id: *, metadata, integrations}, "identificazione.sigla_marca.value.code">, null | undefined>|GetFieldType<{id: *, metadata, integrations}, "identificazione.sigla_marca.value.code">|null|string}
 */
export function getDetailValue(
    detail,
    prop,
    locale,
    productProperties,
    i18n,
    priceDecimalPrecision = 4
) {
    if (typeof detail === 'undefined') {
        return null;
    }

    const state = store.getState();
    const showFilterCode = selectors.getShowFilterCode(state)
    const filterCodeFieldList = selectors.getFilterCodeList(state)

    // FIXME: Sarebbe di type "collection" ma deve funzionare in maniera diversa dallo standard, come da richiesta
    if (prop.code === 'custom_classifications') {
        return detail.children
            .map((item) => {
                return getDetailValue(
                    item,
                    productProperties[item.property],
                    locale,
                    productProperties,
                    i18n,
                    priceDecimalPrecision
                );
            })
            .join('<br/>');
    }

    if (prop.type === 'collection') {
        if (detail.value === null && detail.children && detail.children.length > 0) {
            return detail.children
                .map((item) => {
                    // Ricorsivo
                    return getDetailValue(
                        item,
                        productProperties[item.property],
                        locale,
                        productProperties,
                        i18n,
                        priceDecimalPrecision
                    );
                })
                .join(' / ');
        }

        if (detail.value) {
            // TODO: da verificare con altri tipi di dato...
            return sortBy(detail.value, 'descrizione')
                .map((v) => (isObject(v.descrizione) ? v.descrizione[locale] : v.descrizione))
                .join(' / ');
        }
    }

    if (detail.value === null) {
        return null;
    }

    const numberIntl = getIntlNumberFormat('it-IT', {
        minimumFractionDigits: priceDecimalPrecision,
        maximumFractionDigits: priceDecimalPrecision,
    });

    const priceProperties = [
        'prezzo_listino',
        'prezzo_listino_netto',
        'prezzo_listino_rivenditore',
        'prezzo_dettaglio_raccomandato',
        'prezzo_articolo_pallettizzato',
        'prezzo_precedente',
    ];

    // TODO: da rimuovere non appena il mode "prezzo" verra pubblicato in produzione
    if (includes(priceProperties, prop.code)) {
        if (priceDecimalPrecision === 0) {
            return numberIntl.format(Math.trunc(detail.value));
        }

        return numberIntl.format(detail.value);
    }

    if (prop.type === 'boolean') {
        const s = detail.value === false ? i18n._('no') : i18n._('yes');

        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    if (prop.type === 'single_option') {
        return detail.value.descrizione;
    }

    if (prop.mode === 'marca') {
        return detail.value.sigla_marca;
    }

    if (prop.mode === 'prezzo') {
        return numberIntl.format(detail.value);
    }

    if (prop.type === 'float' || prop.type === 'float_metric_value') {
        return baseIntl.format(detail.value);
    }

    if (prop.type === 'datetime' || prop.type === 'date') {
        if (!detail.value) return null;
        return format(detail.value, 'DD/MM/YYYY');
    }

    if (prop.type === 'key_value_collection') {
        return formatKeyValue(detail.value);
    }

    if (prop.mode === 'territorio_collection') {
        return detail.value
            .map((record) => record.label[locale].value)
            .sort()
            .join(' / ');
    }

    // FIXME: Sarebbe di type "choice" ma deve funzionare in maniera diversa dallo standard, come da richiesta
    if (prop.code === 'sigla_marca') {
        return detail.value.code;
    }

    if (prop.type === 'choice') {
        return isObject(detail.value.label)
            ? get(detail.value.label, locale, detail.value.label.key)
            : detail.value.label;
    }

    if (prop.type === 'localized_string' || prop.type === 'localized_text') {
        return get(detail.value, locale, detail.value.key);
    }

    if (prop.type === 'free_choice') {
        const propsNeedCode = ['linea', 'modello', 'single_serie', 'classe_categoria_sconto_vendita', 'classe_prodotto', 'categoria_merceologica']
        const labelFallback = detail.value?.label?.key ?? detail.value?.code ?? null

        if(!detail.value?.label) return labelFallback

        if(!detail.value.code) return get(detail.value.label, locale, labelFallback)

        if(!detail.value.code && !showFilterCode) return get(detail.value.label, locale, labelFallback)

        if(showFilterCode && !filterCodeFieldList.includes(prop.code)) return get(detail.value.label, locale, labelFallback)

        if(propsNeedCode.includes(prop.code)) {
            return detail.value.code + ' - ' + get(detail.value.label, locale, labelFallback);
        }

        return get(detail.value.label, locale, labelFallback);
    }

    if (prop.type === 'classification') {
        const classificationTree = detail.value.map((leaf) => {
            return get(leaf, `label.${locale}`, `[${leaf.code}]`);
        });

        const classificationLeafs = tail(classificationTree).join(' / ');

        return `${classificationTree[0]}: ${classificationLeafs}`;
    }

    if (prop.type === 'etim_class') {
        return get(detail.value.label, locale, detail.value.name);
    }
    return detail.value;
}
